"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type Context,
  type ReactNode,
} from "react";
import {
  LDReactContext,
  useBoolVariation,
  type LDReactClient,
  type LDReactClientContextValue,
} from "@launchdarkly/react-sdk";

type BooleanFlagOverrides = Readonly<Record<string, boolean>>;

type FeatureFlagRuntime = {
  overrides?: BooleanFlagOverrides;
  reactContext: Context<LDReactClientContextValue>;
  source: "launchdarkly" | "override";
};

const noOpClient = {
  isReady: () => false,
  off: () => undefined,
  on: () => undefined,
} as unknown as LDReactClient;

const NoOpLaunchDarklyContext = createContext<LDReactClientContextValue>({
  client: noOpClient,
  initializedState: "initializing",
});

const FeatureFlagRuntimeContext = createContext<FeatureFlagRuntime | null>(
  null,
);

/**
 * Dispatched on `window` after replacing `window.__LD_FLAG_OVERRIDES__` so E2E
 * tests can switch a flag live, the way a LaunchDarkly stream update would.
 */
export const FLAG_OVERRIDES_CHANGE_EVENT = "ld-flag-overrides-change";

function subscribeToOverrides(onChange: () => void) {
  window.addEventListener(FLAG_OVERRIDES_CHANGE_EVENT, onChange);
  return () =>
    window.removeEventListener(FLAG_OVERRIDES_CHANGE_EVENT, onChange);
}

const getOverrides = () => window.__LD_FLAG_OVERRIDES__;
const getServerOverrides = () => undefined;

/**
 * Connects application hooks to the globally mounted LaunchDarkly provider and
 * applies deterministic browser overrides.
 */
export function FeatureFlagsBridge({ children }: { children: ReactNode }) {
  const overrides = useSyncExternalStore(
    subscribeToOverrides,
    getOverrides,
    getServerOverrides,
  );
  const runtime = useMemo<FeatureFlagRuntime>(
    () => ({
      overrides,
      reactContext: LDReactContext,
      source: "launchdarkly",
    }),
    [overrides],
  );

  return (
    <FeatureFlagRuntimeContext.Provider value={runtime}>
      {children}
    </FeatureFlagRuntimeContext.Provider>
  );
}

export function FeatureFlagsTestProvider({
  children,
  flags = {},
}: {
  children: ReactNode;
  flags?: BooleanFlagOverrides;
}) {
  const runtime = useMemo<FeatureFlagRuntime>(
    () => ({
      overrides: flags,
      reactContext: NoOpLaunchDarklyContext,
      source: "override",
    }),
    [flags],
  );

  return (
    <FeatureFlagRuntimeContext.Provider value={runtime}>
      {children}
    </FeatureFlagRuntimeContext.Provider>
  );
}

/**
 * The deterministic browser or test override for a flag, if one is installed.
 * Unlike `useBooleanFeatureFlag`, this never consults LaunchDarkly.
 */
export function useFeatureFlagOverride(flagKey: string): boolean | undefined {
  const override = useContext(FeatureFlagRuntimeContext)?.overrides?.[flagKey];
  return typeof override === "boolean" ? override : undefined;
}

let didWarnMissingProvider = false;

export function __resetMissingFeatureFlagsProviderWarningForTests(): void {
  didWarnMissingProvider = false;
}

/**
 * Reads a boolean flag by its exact LaunchDarkly key. Missing flags fail closed
 * to the supplied default (`false` by default).
 */
export function useBooleanFeatureFlag(
  flagKey: string,
  defaultValue = false,
): boolean {
  const runtime = useContext(FeatureFlagRuntimeContext);
  const isMissingProvider = runtime === null;
  const reactContext = runtime?.reactContext ?? NoOpLaunchDarklyContext;
  const launchDarklyValue = useBoolVariation(
    flagKey,
    defaultValue,
    reactContext,
  );

  useEffect(() => {
    if (
      process.env.NODE_ENV === "development" &&
      isMissingProvider &&
      !didWarnMissingProvider
    ) {
      didWarnMissingProvider = true;
      console.warn(
        "[useBooleanFeatureFlag] called outside the LaunchDarkly provider; returning the default value.",
      );
    }
  }, [isMissingProvider]);

  const override = runtime?.overrides?.[flagKey];
  if (typeof override === "boolean") return override;

  return runtime?.source === "launchdarkly" ? launchDarklyValue : defaultValue;
}
